import {
  Transaction,
  TransactionTypeEnum,
  TravelWalletOffer,
} from "redmond";

interface SpendAndGetOfferMilestoneMetadata {
  boundary: number;
  rewardToEarn: number;
}

export interface SpendAndGetOfferTrackingData {
  progress: number;
  maxCredits: number;
  cancelled: boolean;
}

const getSpendAndGetOfferMilestones = (
  description: string
): SpendAndGetOfferMilestoneMetadata[] => {
  const milestones = description.replace("HSP_SPEND_AND_EARN=", "").split("|");
  const offerMilestones = milestones.map((milestone) => {
    const milestoneMetadata = milestone.split("-");
    return {
      boundary: Number(milestoneMetadata[0]),
      rewardToEarn: Number(milestoneMetadata[1]),
    };
  });
  return [{ boundary: 0, rewardToEarn: 0 }, ...offerMilestones];
};

const getEarnedCredits = (creditHistory: Transaction[]) => {
  const spendAndGetCredits = creditHistory.filter(
    (credit) =>
      credit.transactionType === TransactionTypeEnum.SpendAndGetCredit
  );

  const spendAndGetCreditIds = new Set(
    spendAndGetCredits
      .map((credit) => credit.statementCreditContext?.capOneCreditId?.value)
      .filter(Boolean)
  );

  const cancelledCredits = creditHistory.filter(
    (credit) =>
      credit.transactionType ===
        TransactionTypeEnum.SpendAndGetCreditCanceled &&
      credit.statementCreditContext?.capOneCreditId?.value &&
      spendAndGetCreditIds.has(
        credit.statementCreditContext.capOneCreditId.value
      )
  );

  const earnedAmount = spendAndGetCredits.reduce(
    (sum, credit) => sum + credit.amountUsd,
    0
  );

  const cancelledAmount = cancelledCredits.reduce(
    (sum, credit) => sum + credit.amountUsd,
    0
  );

  return {
    totalCreditsEarned: earnedAmount + cancelledAmount,
    cancelledAmount: Math.abs(cancelledAmount),
  };
};

export const calculateSpendAndGetOfferTrackingData = (
  creditHistory: Transaction[] | null | undefined,
  offers: TravelWalletOffer[] | null | undefined,
  onPortalSpendAmountDuringOffer: number | null | undefined
): SpendAndGetOfferTrackingData | null => {
  if (creditHistory == null || offers == null) {
    return null;
  }

  let totalSpend = onPortalSpendAmountDuringOffer ?? 0;

  const spendAndGetOffer = offers.find((offer) =>
    offer.descriptions.some((description) =>
      description.startsWith("HSP_SPEND_AND_EARN")
    )
  );
  const spendAndGetOfferDescription = spendAndGetOffer?.descriptions.find(
    (description) => description.startsWith("HSP_SPEND_AND_EARN")
  );
  if (!spendAndGetOfferDescription) {
    return null;
  }

  const { totalCreditsEarned, cancelledAmount } =
    getEarnedCredits(creditHistory);
  const offerMilestones = getSpendAndGetOfferMilestones(
    spendAndGetOfferDescription
  );
  const maxCreditAmount = offerMilestones.reduce(
    (sum, milestone) => sum + milestone.rewardToEarn,
    0
  );

  const isCancelled = cancelledAmount === maxCreditAmount;

  if (isCancelled) {
    return {
      progress: 0,
      maxCredits: maxCreditAmount,
      cancelled: true,
    };
  }

  let cumulativeRewards = 0;
  let nextIncompleteMilestoneIndex = -1;
  for (let i = 0; i < offerMilestones.length; i++) {
    const isComplete =
      totalCreditsEarned >=
      offerMilestones[i].rewardToEarn + cumulativeRewards;
    if (!isComplete && i > 0) {
      nextIncompleteMilestoneIndex = i;
      break;
    }
    cumulativeRewards += offerMilestones[i].rewardToEarn;
  }

  let previousMilestoneRewards = 0;
  const mappedMilestones = offerMilestones.map((milestone, index) => {
    const isCompleted =
      totalCreditsEarned >= milestone.rewardToEarn + previousMilestoneRewards;
    if (isCompleted && onPortalSpendAmountDuringOffer === 0) {
      totalSpend += milestone.boundary;
    }
    const result = {
      position: index * 50,
      completed: isCompleted,
    };
    previousMilestoneRewards += milestone.rewardToEarn;
    return result;
  });

  if (nextIncompleteMilestoneIndex === -1) {
    return {
      progress: 100,
      maxCredits: maxCreditAmount,
      cancelled: false,
    };
  }

  let cumulativeBoundaryToIncomplete = 0;
  for (let i = 0; i < nextIncompleteMilestoneIndex; i++) {
    cumulativeBoundaryToIncomplete += offerMilestones[i].boundary;
  }

  const spentTowardsIncomplete = totalSpend - cumulativeBoundaryToIncomplete;
  const incompleteMilestoneBoundary =
    offerMilestones[nextIncompleteMilestoneIndex].boundary;

  const segmentProgress =
    incompleteMilestoneBoundary > 0
      ? spentTowardsIncomplete / incompleteMilestoneBoundary
      : 0;

  const prevPosition =
    mappedMilestones[nextIncompleteMilestoneIndex - 1]?.position ?? 0;
  const currentPosition =
    mappedMilestones[nextIncompleteMilestoneIndex].position;
  const segmentRange = currentPosition - prevPosition;

  const progress = prevPosition + segmentProgress * segmentRange;

  return {
    progress: Math.min(100, Math.max(0, progress)),
    maxCredits: maxCreditAmount,
    cancelled: false,
  };
};
